/**
 * WorldMap (Sirus 3.3.5a) — R232q22.
 *
 * Полноэкранная карта мира в стиле WoW 3.3.5a (M-key). Три уровня зума,
 * по умолчанию открывается на «Зона» — текущая зона игрока с его стрелкой,
 * квест-маркерами и POI. Юзер может нажать «Континент» (показывает все
 * зоны континента полигонами), затем «Мир» (континенты целиком). Drill-down
 * кликом по зоне/континенту, drill-up — стрелкой назад или табом.
 *
 * Открытие:
 *   • Клавиша M (или русская Ь) — toggle.
 *   • Window event `aiwari-toggle-worldmap` — toggle.
 *   • Esc — закрыть.
 *
 * Гейт: только когда `useUI.stage === 'world'`. Если SirusHud не смонтирован
 * (другая стадия), компонент возвращает null до того, как обработчики что-то
 * сделают.
 *
 * Что НЕ делает (deferred, см. финальный отчёт):
 *   • Реальные арт-фоны континентов и мира (placeholder процедурный пергамент).
 *   • POI — приходят пустыми из `zones.ts.pois`; будем сидить позже.
 *   • Полигональные границы зон — пока используем bbox-прямоугольники
 *     (есть TODO-точка ниже про zones.ts.polygon).
 *   • Анимации между уровнями зума (instant switch).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNet } from '../../state/net-store.js';
import { useQuestPoi, type QuestPoi } from '../../state/quest-poi-store.js';
import { useUI } from '../../state/store.js';
import { useModalStackRegister } from '../../state/active-modals-store.js';
import { ZONES, zoneAt, zoneById, type Zone, type ZonePoi, type ZonePoiKind } from '../../data/zones.js';
import { SirusFrame } from './SirusFrame.js';

// ── Геометрия ────────────────────────────────────────────────────────────
/** Внешние габариты модалки (px). Нечто среднее между WoW WorldMapFrame
 *  и Sirus-портретом — 960×640 хорошо садится на 1080p и оставляет HUD
 *  прозрачно-чёрный задник. */
const MODAL_W = 960;
const MODAL_H = 640;

/** Размер канваса/SVG-зоны (внутри модалки). Считаем от MODAL_W минус
 *  паддинги SirusFrame (`size="lg"` = 26px) + рамка для топ-табов / низа. */
const VIEW_W = MODAL_W - 26 * 2;
const VIEW_H = MODAL_H - 26 * 2 - 90; // 50 top-tabs + 40 bottom-bar

// ── Континенты (мета для Level 2/3) ──────────────────────────────────────
/**
 * Мы не вводим отдельную таблицу континентов — определяем их по
 * `tidal-spawn` / `anchorless-spawn` записям из ZONES + центральный
 * `stormveil-crossroads` показывается как разделительный мост.
 *
 * `id` ниже — стабильный ключ для Level 2 view. `bounds` определяют
 * world-space-bbox континента, в который мы маппим зоны при рендере.
 */
interface Continent {
  id: string;
  name: { ru: string };
  /** id-зоны из ZONES, чьи bounds описывают весь континент. */
  rootZoneId: string;
  /** id-зон, отображаемых внутри континента (sub-zones + cities). */
  childZoneIds: string[];
}

/** Чисто-функциональный список — без runtime-сайд-эффектов и без сети. */
function listContinents(): Continent[] {
  const tidal = zoneById('tidal-spawn');
  const anchor = zoneById('anchorless-spawn');
  if (!tidal || !anchor) return [];
  // Сортируем child-зоны по близости к центру континента (для tooltip-порядка).
  const inBounds = (z: Zone, root: Zone): boolean => {
    const cx = (z.bounds.minX + z.bounds.maxX) / 2;
    const cz = (z.bounds.minZ + z.bounds.maxZ) / 2;
    return cx >= root.bounds.minX && cx <= root.bounds.maxX
        && cz >= root.bounds.minZ && cz <= root.bounds.maxZ;
  };
  const childIds = (root: Zone): string[] =>
    ZONES.filter((z) => z.id !== root.id && inBounds(z, root))
         .map((z) => z.id);
  return [
    { id: 'greenwald', name: { ru: 'Зелёный Дол' }, rootZoneId: tidal.id, childZoneIds: childIds(tidal) },
    { id: 'sharpcrest', name: { ru: 'Острогривая Степь' }, rootZoneId: anchor.id, childZoneIds: childIds(anchor) },
  ];
}

/** Какому континенту принадлежит зона (по центру bbox). null = неопределено. */
function continentOfZone(zoneId: string, conts: Continent[]): Continent | null {
  for (const c of conts) {
    if (c.rootZoneId === zoneId) return c;
    if (c.childZoneIds.includes(zoneId)) return c;
  }
  return null;
}

// ── POI legend ───────────────────────────────────────────────────────────
const POI_LABELS: Record<ZonePoiKind, string> = {
  questgiver: 'Квестодатель',
  turnin: 'Сдать квест',
  city: 'Город',
  flight: 'Полётный мастер',
  dungeon: 'Подземелье',
  mail: 'Почтовый ящик',
  trainer: 'Учитель',
};

// ── Уровни зума ──────────────────────────────────────────────────────────
type ViewLevel = 'zone' | 'continent' | 'world';

// ── Утилиты пространства мира → пиксели ──────────────────────────────────
/** Маппит world-space точку (x,z) в SVG/canvas-координаты внутри bbox. */
function worldToView(
  wx: number,
  wz: number,
  bounds: Zone['bounds'],
  viewW: number,
  viewH: number,
): { x: number; y: number } {
  const fx = (wx - bounds.minX) / Math.max(1, bounds.maxX - bounds.minX);
  const fz = (wz - bounds.minZ) / Math.max(1, bounds.maxZ - bounds.minZ);
  return { x: fx * viewW, y: fz * viewH };
}

// R232q86zbp: hash-based deterministic noise → стабильный organic polygon
// per-zone (юзер: «не квадратами, а по границам карты»). Каждая зона
// выглядит как неровная клякса, повторяющаяся между рендерами.
function hashStr(s: string): number {
  let h = 2166136261 >>> 0;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = (h + ((h << 1) + (h << 4) + (h << 7) + (h << 8) + (h << 24))) >>> 0;
  }
  return h >>> 0;
}

/** Generates an organic 16-point polygon around the bbox of a zone in
 *  view-coordinates, with deterministic per-zone noise. Returns an SVG
 *  `points="x1,y1 x2,y2 …"` string suitable for <polygon points={...}>. */
function organicPolygonPoints(
  zoneId: string,
  tlX: number,
  tlY: number,
  w: number,
  h: number,
  segments = 16,
): string {
  const cx = tlX + w / 2;
  const cy = tlY + h / 2;
  const rx = w / 2;
  const ry = h / 2;
  // Stretch a tiny bit beyond the bbox so neighbouring zones overlap a hair —
  // hover edges feel like they trace painted features, not gridded boxes.
  const pad = 1.06;
  let seed = hashStr(zoneId);
  const rand = () => {
    seed = (seed * 1664525 + 1013904223) >>> 0;
    return seed / 0xffffffff;
  };
  const pts: string[] = [];
  for (let i = 0; i < segments; i++) {
    const t = (i / segments) * Math.PI * 2;
    const wobble = 0.85 + rand() * 0.30; // 0.85..1.15 organic noise per vertex
    const px = cx + Math.cos(t) * rx * pad * wobble;
    const py = cy + Math.sin(t) * ry * pad * wobble;
    pts.push(`${px.toFixed(1)},${py.toFixed(1)}`);
  }
  return pts.join(' ');
}

// ── Компонент ────────────────────────────────────────────────────────────
export function WorldMap() {
  const stage = useUI((s) => s.stage);

  const [open, setOpen] = useState(false);
  const [viewLevel, setViewLevel] = useState<ViewLevel>('zone');
  const [zoom, setZoom] = useState<number>(1); // 1..4 для zone-view
  const [pan, setPan] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const [hoverPoi, setHoverPoi] = useState<{ poi: ZonePoi; sx: number; sy: number } | null>(null);
  const [hoverZoneId, setHoverZoneId] = useState<string | null>(null);
  const [hoverContinentId, setHoverContinentId] = useState<string | null>(null);
  /** Зона, в которую юзер «зашёл» через drill-down с континента.
   *  Если null — на «Зона»-уровне рендерим текущую зону игрока. */
  const [drilledZoneId, setDrilledZoneId] = useState<string | null>(null);
  /** Континент, в который юзер «зашёл» с «Мир»-уровня. null = по умолчанию
   *  показываем континент игрока. */
  const [drilledContinentId, setDrilledContinentId] = useState<string | null>(null);
  /** Sirus 1:1 toolbar: «Карта игровой зоны» mode toggle. По умолчанию
   *  показываем POI / квест-маркеры на карте; «Не отображать» — скрывает. */
  const [showQuestMarkers, setShowQuestMarkers] = useState<boolean>(true);
  /** Какой dropdown открыт сейчас (single-open behaviour, как в Sirus). */
  const [openDropdown, setOpenDropdown] = useState<null | 'mode' | 'continent' | 'zone'>(null);

  // ── Данные игрока (тонкая подписка) ─────────────────────────────────
  const self = useNet((s) => (s.selfId ? s.players[s.selfId] : null));

  // ── Pan drag state ─────────────────────────────────────────────────
  const dragRef = useRef<{ active: boolean; sx: number; sy: number; px: number; py: number }>({
    active: false, sx: 0, sy: 0, px: 0, py: 0,
  });

  // ── Открытие / закрытие через клавиши + window event ───────────────
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement | null)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA') return;
      // Латиница 'm/M' и кириллица 'ь/Ь' (на той же физической клавише).
      if (e.key === 'm' || e.key === 'M' || e.key === 'ь' || e.key === 'Ь') {
        if (stage !== 'world') return;
        e.preventDefault();
        setOpen((o) => !o);
      } else if (e.key === 'Escape' && open) {
        e.preventDefault();
        setOpen(false);
      }
    };
    const onToggleEvt = () => {
      if (stage !== 'world') return;
      setOpen((o) => !o);
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener('aiwari-toggle-worldmap', onToggleEvt);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('aiwari-toggle-worldmap', onToggleEvt);
    };
  }, [open, stage]);

  // Регистрация в общем modal-stack (Esc через ESC-handler работает).
  useModalStackRegister('worldmap', open, () => setOpen(false));

  // Ресет drill-down при закрытии — следующее открытие снова возвращает
  // на «Зона» с актуальной зоной игрока.
  useEffect(() => {
    if (!open) {
      setDrilledZoneId(null);
      setDrilledContinentId(null);
      setHoverPoi(null);
      setHoverZoneId(null);
      setHoverContinentId(null);
      setPan({ x: 0, y: 0 });
      setZoom(1);
      setViewLevel('zone');
      setOpenDropdown(null);
    }
  }, [open]);

  // Закрытие dropdown'а при клике вне — глобальный slug listener.
  useEffect(() => {
    if (!openDropdown) return;
    const onDocClick = (e: MouseEvent) => {
      const t = e.target as HTMLElement | null;
      if (t && t.closest && t.closest('.wm-dd')) return;
      setOpenDropdown(null);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [openDropdown]);

  // ── Текущая зона игрока ────────────────────────────────────────────
  const playerZone = useMemo<Zone | null>(() => {
    if (!self) return null;
    if (self.zoneId) {
      const byId = zoneById(self.zoneId);
      if (byId) return byId;
    }
    return zoneAt(self.pos.x, self.pos.z);
  }, [self]);

  const continents = useMemo(listContinents, []);
  const playerContinent = useMemo(() => {
    if (!playerZone) return continents[0] ?? null;
    return continentOfZone(playerZone.id, continents) ?? continents[0] ?? null;
  }, [playerZone, continents]);

  // Какая зона показывается на «Зона»-уровне
  const activeZone: Zone | null = useMemo(() => {
    if (drilledZoneId) {
      const z = zoneById(drilledZoneId);
      if (z) return z;
    }
    return playerZone;
  }, [drilledZoneId, playerZone]);

  // Какой континент показывается на «Континент»-уровне
  const activeContinent: Continent | null = useMemo(() => {
    if (drilledContinentId) {
      const c = continents.find((x) => x.id === drilledContinentId);
      if (c) return c;
    }
    return playerContinent;
  }, [drilledContinentId, playerContinent, continents]);

  // ── Mousewheel зум (только на «Зона») ──────────────────────────────
  const onWheel = useCallback((e: React.WheelEvent) => {
    if (viewLevel !== 'zone') return;
    e.stopPropagation();
    const dir = e.deltaY < 0 ? +1 : -1;
    setZoom((z) => Math.max(1, Math.min(4, z + dir)));
  }, [viewLevel]);

  // ── Pan drag (только на «Зона», только пока zoom>1) ────────────────
  const onPointerDown = useCallback((e: React.PointerEvent) => {
    if (viewLevel !== 'zone' || zoom <= 1) return;
    (e.currentTarget as Element).setPointerCapture?.(e.pointerId);
    dragRef.current = { active: true, sx: e.clientX, sy: e.clientY, px: pan.x, py: pan.y };
  }, [viewLevel, zoom, pan]);
  const onPointerMove = useCallback((e: React.PointerEvent) => {
    const d = dragRef.current;
    if (!d.active) return;
    setPan({ x: d.px + (e.clientX - d.sx), y: d.py + (e.clientY - d.sy) });
  }, []);
  const onPointerUp = useCallback((e: React.PointerEvent) => {
    dragRef.current.active = false;
    (e.currentTarget as Element).releasePointerCapture?.(e.pointerId);
  }, []);

  // ── Обновление pan в clamp при смене zoom ──────────────────────────
  useEffect(() => {
    if (zoom === 1) setPan({ x: 0, y: 0 });
  }, [zoom]);

  // ── Drill-up через стрелку «назад» ─────────────────────────────────
  const goUp = useCallback(() => {
    if (viewLevel === 'zone') {
      setViewLevel('continent');
      setHoverPoi(null);
    } else if (viewLevel === 'continent') {
      setViewLevel('world');
    }
  }, [viewLevel]);

  // Гейт рендера. Слушатели остаются на месте даже когда не в `world` —
  // но они сами выходят рано, так что это безопасно.
  if (!open || stage !== 'world') return null;

  // ── Текст в правом верхнем углу: имя текущего вида ─────────────────
  const titleRu: string = (() => {
    if (viewLevel === 'zone') return activeZone?.name.ru ?? 'Неизвестная зона';
    if (viewLevel === 'continent') return activeContinent?.name.ru ?? 'Континент';
    return 'Мир';
  })();

  return (
    <div
      className="aiwari-worldmap-overlay"
      onClick={(e) => { if (e.target === e.currentTarget) setOpen(false); }}
    >
      <SirusFrame size="lg" style={{ width: MODAL_W, height: MODAL_H, position: 'relative' }}>
        {/* ── Sirus 3.3.5a top toolbar: 3 dropdowns + «Отдалить» ─────
         *  Layout (per Sirus 3.3.5a M-key map screenshots):
         *    [⌃ Карта игровой зоны: Не отображать ▾]
         *    [⌃ Континент: <name> ▾]
         *    [⌃ Игровая зона: Вручную ▾]
         *    [● Отдалить] (red gradient button — zoom-out level)
         *  Window controls (_□X) handled by SirusFrame chrome. */}
        <div className="wm-tabs">
          {/* Dropdown 1: «Карта игровой зоны» — toggle quest POI overlay. */}
          <div className="wm-dd">
            <div className="wm-dd-label">Карта игровой зоны</div>
            <button
              type="button"
              className={`wm-dd-pill ${openDropdown === 'mode' ? 'open' : ''}`}
              onClick={() => setOpenDropdown((d) => (d === 'mode' ? null : 'mode'))}
              aria-haspopup="listbox"
              aria-expanded={openDropdown === 'mode'}
            >
              <span className="wm-dd-text">{showQuestMarkers ? 'По карте' : 'Не отображать'}</span>
              <span className="wm-dd-chev" aria-hidden>▾</span>
            </button>
            {openDropdown === 'mode' && (
              <ul className="wm-dd-menu" role="listbox">
                <li
                  role="option"
                  aria-selected={!showQuestMarkers}
                  className={`wm-dd-item ${!showQuestMarkers ? 'sel' : ''}`}
                  onClick={() => { setShowQuestMarkers(false); setOpenDropdown(null); }}
                >Не отображать</li>
                <li
                  role="option"
                  aria-selected={showQuestMarkers}
                  className={`wm-dd-item ${showQuestMarkers ? 'sel' : ''}`}
                  onClick={() => { setShowQuestMarkers(true); setOpenDropdown(null); }}
                >По карте</li>
              </ul>
            )}
          </div>

          {/* Dropdown 2: «Континент» — pick which continent to show. */}
          <div className="wm-dd">
            <div className="wm-dd-label">Континент</div>
            <button
              type="button"
              className={`wm-dd-pill ${openDropdown === 'continent' ? 'open' : ''}`}
              onClick={() => setOpenDropdown((d) => (d === 'continent' ? null : 'continent'))}
              aria-haspopup="listbox"
              aria-expanded={openDropdown === 'continent'}
            >
              <span className="wm-dd-text">{activeContinent?.name.ru ?? '—'}</span>
              <span className="wm-dd-chev" aria-hidden>▾</span>
            </button>
            {openDropdown === 'continent' && (
              <ul className="wm-dd-menu" role="listbox">
                {continents.map((c) => (
                  <li
                    key={c.id}
                    role="option"
                    aria-selected={activeContinent?.id === c.id}
                    className={`wm-dd-item ${activeContinent?.id === c.id ? 'sel' : ''}`}
                    onClick={() => {
                      setDrilledContinentId(c.id);
                      setDrilledZoneId(null);
                      setViewLevel('continent');
                      setHoverPoi(null);
                      setHoverZoneId(null);
                      setHoverContinentId(null);
                      setZoom(1);
                      setPan({ x: 0, y: 0 });
                      setOpenDropdown(null);
                    }}
                  >
                    {c.name.ru}
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Dropdown 3: «Игровая зона» — pick sub-zone of current continent. */}
          <div className="wm-dd">
            <div className="wm-dd-label">Игровая зона</div>
            <button
              type="button"
              className={`wm-dd-pill ${openDropdown === 'zone' ? 'open' : ''}`}
              onClick={() => setOpenDropdown((d) => (d === 'zone' ? null : 'zone'))}
              aria-haspopup="listbox"
              aria-expanded={openDropdown === 'zone'}
              disabled={!activeContinent}
            >
              <span className="wm-dd-text">
                {viewLevel === 'zone' && activeZone ? activeZone.name.ru : 'Вручную'}
              </span>
              <span className="wm-dd-chev" aria-hidden>▾</span>
            </button>
            {openDropdown === 'zone' && activeContinent && (
              <ul className="wm-dd-menu wm-dd-menu-tall" role="listbox">
                <li
                  role="option"
                  aria-selected={viewLevel !== 'zone'}
                  className={`wm-dd-item ${viewLevel !== 'zone' ? 'sel' : ''}`}
                  onClick={() => {
                    setDrilledZoneId(null);
                    setViewLevel('continent');
                    setOpenDropdown(null);
                  }}
                >Вручную</li>
                {activeContinent.childZoneIds
                  .map((id) => zoneById(id))
                  .filter((z): z is Zone => !!z)
                  .map((z) => (
                    <li
                      key={z.id}
                      role="option"
                      aria-selected={activeZone?.id === z.id}
                      className={`wm-dd-item ${activeZone?.id === z.id ? 'sel' : ''}`}
                      onClick={() => {
                        setDrilledZoneId(z.id);
                        setHoverZoneId(z.id);
                        setViewLevel('zone');
                        setZoom(1);
                        setPan({ x: 0, y: 0 });
                        setOpenDropdown(null);
                      }}
                    >
                      {z.name.ru}
                    </li>
                  ))}
              </ul>
            )}
          </div>

          {/* «Отдалить» — red gradient button. Zone→continent→world drill-up. */}
          <button
            type="button"
            className="wm-zoom-out"
            onClick={goUp}
            disabled={viewLevel === 'world'}
            aria-label="Отдалить"
            title="Отдалить (уровень выше)"
          >
            <span className="wm-zoom-out-dot" aria-hidden />
            Отдалить
          </button>

          <div className="wm-title">{titleRu}</div>
        </div>

        {/* ── Тело: канвас зависит от viewLevel ────────────────────── */}
        <div
          className="wm-body"
          onWheel={onWheel}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
        >
          {viewLevel === 'zone' && activeZone && (
            <ZoneView
              zone={activeZone}
              zoom={zoom}
              pan={pan}
              isPlayerHere={!!playerZone && playerZone.id === activeZone.id}
              playerPos={self?.pos ?? null}
              playerHeading={self?.heading ?? 0}
              onHoverPoi={setHoverPoi}
              showQuestMarkers={showQuestMarkers}
            />
          )}
          {viewLevel === 'continent' && activeContinent && (
            <ContinentView
              continent={activeContinent}
              hoverZoneId={hoverZoneId}
              setHoverZoneId={setHoverZoneId}
              playerZoneId={playerZone?.id ?? null}
              onZoneClick={(zid) => {
                setDrilledZoneId(zid);
                setViewLevel('zone');
                setZoom(1);
                setPan({ x: 0, y: 0 });
              }}
            />
          )}
          {viewLevel === 'world' && (
            <WorldView
              continents={continents}
              hoverContinentId={hoverContinentId}
              setHoverContinentId={setHoverContinentId}
              playerContinentId={playerContinent?.id ?? null}
              onContinentClick={(cid) => {
                setDrilledContinentId(cid);
                setViewLevel('continent');
              }}
            />
          )}
          {!activeZone && viewLevel === 'zone' && (
            <div className="wm-empty">Нет данных о зоне игрока</div>
          )}
        </div>

        {/* ── POI Tooltip ─────────────────────────────────────────── */}
        {viewLevel === 'zone' && hoverPoi && (
          <div
            className="wm-tip"
            style={{
              left: hoverPoi.sx + 12,
              top: hoverPoi.sy + 12,
            }}
          >
            <div className="wm-tip-name">{hoverPoi.poi.name}</div>
            <div className="wm-tip-kind">{POI_LABELS[hoverPoi.poi.kind]}</div>
          </div>
        )}

        {/* ── Bottom bar: легенда + zoom +/- + close ──────────────── */}
        <div className="wm-bottom">
          <div className="wm-legend">
            <LegendDot kind="questgiver" />
            <LegendDot kind="turnin" />
            <LegendDot kind="city" />
            <LegendDot kind="flight" />
            <LegendDot kind="dungeon" />
            <LegendDot kind="mail" />
            <LegendDot kind="trainer" />
          </div>
          <div className="wm-bottom-actions">
            <button
              type="button"
              className="wm-mini"
              aria-label="Отдалить"
              onClick={() => setZoom((z) => Math.max(1, z - 1))}
              disabled={viewLevel !== 'zone' || zoom <= 1}
              title="Отдалить"
            >−</button>
            <span className="wm-zoom-label">{viewLevel === 'zone' ? `${zoom}×` : '—'}</span>
            <button
              type="button"
              className="wm-mini"
              aria-label="Приблизить"
              onClick={() => setZoom((z) => Math.min(4, z + 1))}
              disabled={viewLevel !== 'zone' || zoom >= 4}
              title="Приблизить"
            >+</button>
            <button
              type="button"
              className="wm-close"
              aria-label="Закрыть"
              onClick={() => setOpen(false)}
              title="Закрыть (Esc)"
            >×</button>
          </div>
        </div>
      </SirusFrame>

      <style>{`
        .aiwari-worldmap-overlay {
          position: fixed;
          inset: 0;
          z-index: 45;
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(0, 0, 0, 0.65);
          backdrop-filter: blur(2px);
          -webkit-backdrop-filter: blur(2px);
          pointer-events: auto;
        }
        .aiwari-worldmap-overlay .wm-tabs {
          display: flex;
          align-items: flex-end;
          gap: 12px;
          height: 56px;
          padding-bottom: 6px;
          border-bottom: 1px solid var(--aiwari-frame-edge);
        }
        /* Sirus 3.3.5a top-bar dropdown: каждая «pill» имеет крошечный
         *  caption-label сверху + золотую обводку pill-shape с шевроном. */
        .aiwari-worldmap-overlay .wm-dd {
          position: relative;
          display: flex;
          flex-direction: column;
          gap: 2px;
        }
        .aiwari-worldmap-overlay .wm-dd-label {
          font-family: var(--aiwari-font-body);
          font-size: 9px;
          line-height: 1;
          letter-spacing: 0.08em;
          color: var(--aiwari-gold-soft);
          text-transform: none;
          padding-left: 12px;
          opacity: 0.85;
          text-shadow: 0 1px 0 #000;
        }
        .aiwari-worldmap-overlay .wm-dd-pill {
          height: 24px;
          min-width: 150px;
          padding: 0 10px 0 14px;
          display: inline-flex;
          align-items: center;
          justify-content: space-between;
          gap: 8px;
          background: linear-gradient(180deg, #1a1208 0%, #0a0604 100%);
          border: 1px solid var(--aiwari-frame-edge);
          border-radius: 12px;
          color: var(--aiwari-gold-soft);
          font-family: var(--aiwari-font-body);
          font-size: 11px;
          letter-spacing: 0.04em;
          cursor: pointer;
          box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 0.55), 0 1px 0 rgba(0, 0, 0, 0.6);
          transition: border-color 80ms linear, box-shadow 80ms linear, color 80ms linear;
        }
        .aiwari-worldmap-overlay .wm-dd-pill:hover:not(:disabled),
        .aiwari-worldmap-overlay .wm-dd-pill.open {
          border-color: var(--aiwari-gold);
          color: var(--aiwari-gold);
          box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 0.55), 0 0 6px rgba(250, 179, 0, 0.45);
        }
        .aiwari-worldmap-overlay .wm-dd-pill:disabled {
          opacity: 0.45;
          cursor: not-allowed;
        }
        .aiwari-worldmap-overlay .wm-dd-text {
          flex: 1;
          text-align: center;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .aiwari-worldmap-overlay .wm-dd-chev {
          font-size: 10px;
          line-height: 1;
          color: var(--aiwari-gold);
          opacity: 0.85;
        }
        .aiwari-worldmap-overlay .wm-dd-menu {
          position: absolute;
          top: 100%;
          left: 0;
          margin: 4px 0 0;
          padding: 4px 0;
          list-style: none;
          min-width: 100%;
          max-width: 260px;
          max-height: 320px;
          overflow-y: auto;
          background: linear-gradient(180deg, #1a1208 0%, #0a0604 100%);
          border: 1px solid var(--aiwari-gold);
          border-radius: 4px;
          box-shadow: 0 6px 18px rgba(0, 0, 0, 0.85), 0 0 8px rgba(250, 179, 0, 0.25);
          z-index: 5;
        }
        .aiwari-worldmap-overlay .wm-dd-menu-tall {
          max-height: 420px;
        }
        .aiwari-worldmap-overlay .wm-dd-item {
          padding: 5px 14px;
          font-family: var(--aiwari-font-body);
          font-size: 11px;
          letter-spacing: 0.03em;
          color: var(--aiwari-gold-soft);
          cursor: pointer;
          white-space: nowrap;
        }
        .aiwari-worldmap-overlay .wm-dd-item:hover {
          background: rgba(250, 179, 0, 0.14);
          color: var(--aiwari-gold);
        }
        .aiwari-worldmap-overlay .wm-dd-item.sel {
          color: var(--aiwari-gold);
          background: rgba(250, 179, 0, 0.08);
          font-weight: 600;
        }
        /* «Отдалить» — фирменная красная кнопка Sirus map toolbar. */
        .aiwari-worldmap-overlay .wm-zoom-out {
          height: 24px;
          padding: 0 14px;
          display: inline-flex;
          align-items: center;
          gap: 6px;
          background: linear-gradient(180deg, #b22222 0%, #6b0d0d 100%);
          border: 1px solid #ffb27a;
          border-radius: 12px;
          color: #fff5e6;
          font-family: var(--aiwari-font-body);
          font-size: 11px;
          letter-spacing: 0.05em;
          cursor: pointer;
          text-shadow: 0 1px 0 rgba(0, 0, 0, 0.6);
          box-shadow: inset 0 1px 0 rgba(255, 220, 180, 0.25), 0 1px 0 rgba(0, 0, 0, 0.6);
          transition: filter 80ms linear, box-shadow 80ms linear;
        }
        .aiwari-worldmap-overlay .wm-zoom-out:hover:not(:disabled) {
          filter: brightness(1.15);
          box-shadow: inset 0 1px 0 rgba(255, 220, 180, 0.35), 0 0 8px rgba(255, 90, 50, 0.5);
        }
        .aiwari-worldmap-overlay .wm-zoom-out:disabled {
          opacity: 0.4;
          cursor: not-allowed;
        }
        .aiwari-worldmap-overlay .wm-zoom-out-dot {
          width: 7px;
          height: 7px;
          border-radius: 50%;
          background: radial-gradient(circle, #fff5e6 0%, #ff8866 70%);
          box-shadow: 0 0 4px rgba(255, 200, 160, 0.9);
        }
        .aiwari-worldmap-overlay .wm-title {
          margin-left: auto;
          align-self: center;
          font-family: var(--aiwari-font-display);
          font-size: 16px;
          letter-spacing: 0.08em;
          color: var(--aiwari-gold);
          text-shadow: 0 1px 0 #000, 0 0 8px rgba(250, 179, 0, 0.45);
        }
        .aiwari-worldmap-overlay .wm-body {
          position: relative;
          width: ${VIEW_W}px;
          height: ${VIEW_H}px;
          margin-top: 8px;
          background: radial-gradient(circle at center, #2a2114, #0F0A05);
          border: 1px solid var(--aiwari-frame-edge);
          border-radius: 3px;
          overflow: hidden;
          cursor: grab;
          user-select: none;
        }
        .aiwari-worldmap-overlay .wm-body:active {
          cursor: grabbing;
        }
        .aiwari-worldmap-overlay .wm-empty {
          position: absolute;
          inset: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          color: var(--aiwari-fg-muted);
          font-family: var(--aiwari-font-body);
          font-style: italic;
        }
        .aiwari-worldmap-overlay .wm-bottom {
          height: 40px;
          margin-top: 8px;
          padding-top: 8px;
          border-top: 1px solid var(--aiwari-frame-edge);
          display: flex;
          align-items: center;
          gap: 12px;
        }
        .aiwari-worldmap-overlay .wm-legend {
          display: flex;
          gap: 14px;
          align-items: center;
          flex: 1;
          flex-wrap: wrap;
          font-family: var(--aiwari-font-body);
          font-size: 11px;
          color: var(--aiwari-fg-muted);
        }
        .aiwari-worldmap-overlay .wm-legend-dot {
          display: inline-flex;
          align-items: center;
          gap: 4px;
        }
        .aiwari-worldmap-overlay .wm-bottom-actions {
          display: flex;
          align-items: center;
          gap: 4px;
        }
        .aiwari-worldmap-overlay .wm-zoom-label {
          width: 28px;
          text-align: center;
          font-family: var(--aiwari-font-mono);
          font-size: 11px;
          color: var(--aiwari-fg);
        }
        .aiwari-worldmap-overlay .wm-mini,
        .aiwari-worldmap-overlay .wm-close {
          width: 26px;
          height: 26px;
          padding: 0;
          font-family: var(--aiwari-font-display);
          font-size: 14px;
          font-weight: 700;
          line-height: 1;
          color: var(--aiwari-gold);
          background: linear-gradient(180deg, #1a1410, #0B0907);
          border: 1px solid var(--aiwari-frame-edge);
          border-radius: 50%;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: border-color 80ms linear, box-shadow 80ms linear;
        }
        .aiwari-worldmap-overlay .wm-close {
          margin-left: 8px;
          color: var(--aiwari-gold-soft);
        }
        .aiwari-worldmap-overlay .wm-mini:hover:not(:disabled),
        .aiwari-worldmap-overlay .wm-close:hover {
          border-color: var(--aiwari-gold);
          box-shadow: 0 0 6px rgba(250, 179, 0, 0.45);
        }
        .aiwari-worldmap-overlay .wm-mini:disabled {
          opacity: 0.35;
          cursor: not-allowed;
        }
        .aiwari-worldmap-overlay .wm-tip {
          position: fixed;
          z-index: 46;
          padding: 6px 10px;
          background: var(--aiwari-bg-card-solid);
          border: 1px solid var(--aiwari-frame-edge);
          border-radius: 3px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.7);
          pointer-events: none;
          font-family: var(--aiwari-font-body);
          font-size: 12px;
          color: var(--aiwari-fg);
          white-space: nowrap;
        }
        .aiwari-worldmap-overlay .wm-tip-name {
          font-family: var(--aiwari-font-display);
          color: var(--aiwari-gold);
          font-size: 12px;
          letter-spacing: 0.04em;
        }
        .aiwari-worldmap-overlay .wm-tip-kind {
          font-size: 10px;
          color: var(--aiwari-fg-muted);
          margin-top: 1px;
        }
      `}</style>
    </div>
  );
}

// ─── Level 1: Zone view ──────────────────────────────────────────────────
interface ZoneViewProps {
  zone: Zone;
  zoom: number;
  pan: { x: number; y: number };
  isPlayerHere: boolean;
  playerPos: { x: number; y: number; z: number } | null;
  playerHeading: number;
  onHoverPoi: (h: { poi: ZonePoi; sx: number; sy: number } | null) => void;
  /** «Карта игровой зоны» toolbar mode: when false → hide quest POI overlay. */
  showQuestMarkers: boolean;
}

function ZoneView({
  zone, zoom, pan, isPlayerHere, playerPos, playerHeading, onHoverPoi, showQuestMarkers,
}: ZoneViewProps) {
  // Состояние подгрузки фона зоны (`/zones/<id>/full.png`).
  const [bgLoaded, setBgLoaded] = useState(false);
  const [bgError, setBgError] = useState(false);

  // R232q22 Phase 3: динамические Quest POI (зависят от уровня + квестов
  // игрока). Тонкая подписка — Zustand триггерит ре-рендер только когда
  // массив pois реально меняется.
  const questPois = useQuestPoi((s) => s.pois);
  // R232q86zb2: server-replicated NPC quest markers — добавляются к локальному
  // POI-стору. NPC.questMarker вычисляется на сервере NpcRegistry.tickIdle
  // на основе wotlk-quest-registry + per-player progress.
  const replicatedNpcs = useNet((s) => s.npcs);
  const zonePois = useMemo<QuestPoi[]>(() => {
    const local = questPois.filter((p) => p.zoneId === zone.id);
    // Build accepted-giver set so we can promote in-progress markers when
    // the server marker is empty but we know we're carrying their quest.
    let acceptedGivers = new Set<string>();
    try {
      const acc = JSON.parse(localStorage.getItem('aiwari-quest-state:accepted') ?? '[]') as Array<{ giver?: string }>;
      acceptedGivers = new Set(acc.map((a) => a.giver).filter(Boolean) as string[]);
    } catch { /* */ }
    const fromServer: QuestPoi[] = [];
    for (const id of Object.keys(replicatedNpcs)) {
      const npc = replicatedNpcs[id];
      if (!npc) continue;
      if (npc.npcType !== 'questGiver') continue;
      if (npc.zoneId !== zone.id) continue;
      const marker = npc.questMarker ?? '';
      let kind: QuestPoi['kind'] | null = null;
      if (marker.includes('?')) kind = 'turnin';
      else if (marker.includes('!')) kind = 'available';
      else if (acceptedGivers.has(id)) kind = 'in-progress';
      if (!kind) continue;
      fromServer.push({
        npcId: id,
        npcName: npc.displayName,
        zoneId: npc.zoneId,
        x: npc.pos.x,
        z: npc.pos.z,
        kind,
        questIds: [],
        minLevel: 1,
      });
    }
    return [...local, ...fromServer];
  }, [questPois, zone.id, replicatedNpcs]);

  // R232q86zbd: track loaded URL — fallback chain may pick .jpg/.png/world-map
  const [bgLoadedUrl, setBgLoadedUrl] = useState<string>('');
  useEffect(() => {
    setBgLoaded(false);
    setBgError(false);
    setBgLoadedUrl('');
    const candidates = [
      `${import.meta.env.BASE_URL}maps/zone-${zone.id}.png`,
      `${import.meta.env.BASE_URL}maps/zone-${zone.id}.jpg`,
      `${import.meta.env.BASE_URL}zones/${zone.id}/full.png`,
      `${import.meta.env.BASE_URL}maps/aiwari-world-map.png`,
    ];
    let idx = 0;
    let cancelled = false;
    const tryNext = () => {
      if (cancelled) return;
      if (idx >= candidates.length) {
        setBgError(true);
        return;
      }
      const url = candidates[idx]!;
      const img = new Image();
      img.decoding = 'async';
      img.onload = () => {
        if (!cancelled) {
          setBgLoaded(true);
          setBgLoadedUrl(url);
        }
      };
      img.onerror = () => { idx++; tryNext(); };
      img.src = url;
    };
    tryNext();
    return () => { cancelled = true; };
  }, [zone.id]);

  /** Тик 30Hz — чтобы стрелка игрока перерисовывалась плавно даже без новых
   *  network-сообщений (например, между тиками 80ms). */
  const [, setRafTick] = useState(0);
  useEffect(() => {
    let raf = 0;
    const loop = () => {
      setRafTick((t) => (t + 1) & 0xff);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, []);

  // Position of player in view-coords (если он в этой зоне).
  const playerView = useMemo(() => {
    if (!isPlayerHere || !playerPos) return null;
    return worldToView(playerPos.x, playerPos.z, zone.bounds, VIEW_W, VIEW_H);
  }, [isPlayerHere, playerPos, zone]);

  const bgUrl = bgLoadedUrl;
  const showBgImg = bgLoaded && !bgError && !!bgLoadedUrl;

  // Контент масштабируется + пан как единая «карта»-плёнка.
  const contentTransform = `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`;

  return (
    <svg
      width={VIEW_W}
      height={VIEW_H}
      viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
      style={{ display: 'block' }}
    >
      <defs>
        <pattern id="wm-parchment" patternUnits="userSpaceOnUse" width="180" height="180">
          <rect width="180" height="180" fill="#2a2114" />
          <circle cx="40" cy="60" r="22" fill="rgba(232,199,123,0.04)" />
          <circle cx="120" cy="120" r="34" fill="rgba(232,199,123,0.03)" />
        </pattern>
        <radialGradient id="wm-zone-vignette" cx="50%" cy="50%" r="50%">
          <stop offset="60%" stopColor="rgba(0,0,0,0)" />
          <stop offset="100%" stopColor="rgba(0,0,0,0.45)" />
        </radialGradient>
      </defs>

      <g style={{ transform: contentTransform, transformOrigin: '0 0' }}>
        {/* Фон: либо изображение зоны, либо пергамент-fallback. */}
        {showBgImg ? (
          <image href={bgUrl} x={0} y={0} width={VIEW_W} height={VIEW_H} preserveAspectRatio="xMidYMid slice" />
        ) : (
          <>
            <rect x={0} y={0} width={VIEW_W} height={VIEW_H} fill="url(#wm-parchment)" />
            {/* Контур зоны для контекста (даже без арта). */}
            <rect
              x={8}
              y={8}
              width={VIEW_W - 16}
              height={VIEW_H - 16}
              fill="none"
              stroke={zone.colour}
              strokeOpacity={0.45}
              strokeWidth={2}
              strokeDasharray="4 4"
            />
            <text
              x={VIEW_W / 2}
              y={VIEW_H / 2}
              textAnchor="middle"
              fill="rgba(232,199,123,0.18)"
              fontFamily="var(--aiwari-font-display)"
              fontSize={64}
              letterSpacing="0.1em"
            >
              {zone.name.ru}
            </text>
          </>
        )}

        {/* Vignette */}
        <rect x={0} y={0} width={VIEW_W} height={VIEW_H} fill="url(#wm-zone-vignette)" />

        {/* POIs — gated by «Карта игровой зоны» toolbar mode (Sirus 1:1). */}
        {showQuestMarkers && (zone.pois ?? []).map((poi, i) => {
          const v = worldToView(poi.x, poi.z, zone.bounds, VIEW_W, VIEW_H);
          return (
            <g
              key={`${poi.kind}-${i}`}
              transform={`translate(${v.x}, ${v.y})`}
              style={{ cursor: 'pointer' }}
              onMouseEnter={(e) => onHoverPoi({ poi, sx: e.clientX, sy: e.clientY })}
              onMouseMove={(e) => onHoverPoi({ poi, sx: e.clientX, sy: e.clientY })}
              onMouseLeave={() => onHoverPoi(null)}
            >
              <PoiGlyph kind={poi.kind} />
            </g>
          );
        })}

        {/* R232q22 Phase 3: динамические Quest POI (от уровня + accepted/completed). */}
        {showQuestMarkers && zonePois.map((p) => {
          const v = worldToView(p.x, p.z, zone.bounds, VIEW_W, VIEW_H);
          // Перенаправляем hover в общий POI-tooltip как questgiver/turnin —
          // это позволяет переиспользовать tooltip-механику без новой ветки UI.
          const fakeKind: ZonePoiKind = p.kind === 'turnin' ? 'turnin' : 'questgiver';
          const tipPoi: ZonePoi = { kind: fakeKind, x: p.x, z: p.z, name: p.npcName };
          return (
            <g
              key={`q-${p.npcId}`}
              transform={`translate(${v.x}, ${v.y})`}
              style={{ cursor: 'pointer' }}
              onMouseEnter={(e) => onHoverPoi({ poi: tipPoi, sx: e.clientX, sy: e.clientY })}
              onMouseMove={(e) => onHoverPoi({ poi: tipPoi, sx: e.clientX, sy: e.clientY })}
              onMouseLeave={() => onHoverPoi(null)}
            >
              <DynamicQuestGlyph kind={p.kind} />
            </g>
          );
        })}

        {/* Стрелка игрока — золотой треугольник с heading */}
        {playerView && (
          <g transform={`translate(${playerView.x}, ${playerView.y}) rotate(${(playerHeading * 180) / Math.PI})`}>
            <circle r={9} fill="rgba(250,179,0,0.18)" />
            <polygon
              points="0,-8 6,7 0,4 -6,7"
              fill="#FAB300"
              stroke="#0F0900"
              strokeWidth={1.2}
            />
          </g>
        )}
      </g>
    </svg>
  );
}

// ─── Level 2: Continent view ─────────────────────────────────────────────
interface ContinentViewProps {
  continent: Continent;
  hoverZoneId: string | null;
  setHoverZoneId: (id: string | null) => void;
  playerZoneId: string | null;
  onZoneClick: (zoneId: string) => void;
}

function ContinentView({
  continent, hoverZoneId, setHoverZoneId, playerZoneId, onZoneClick,
}: ContinentViewProps) {
  const root = zoneById(continent.rootZoneId);
  const childZones: Zone[] = useMemo(() => {
    return continent.childZoneIds
      .map((id) => zoneById(id))
      .filter((z): z is Zone => !!z);
  }, [continent]);

  if (!root) return null;

  // Каждая дочерняя зона рисуется bbox-прямоугольником (TODO: полигоны
  // из zones.ts.polygon, когда введём их). Преобразуем world-bbox зоны в
  // view-coordinates через bounds континента.
  const tooltip = (() => {
    if (!hoverZoneId) return null;
    const z = zoneById(hoverZoneId);
    if (!z) return null;
    return z;
  })();

  // R232q86zbp: painted parchment continent map (Nano Banana Pro generated,
  // saved to /public/maps/continent-{id}.png). Юзер 2026-05-09:
  // «не делай карту квадратами — делай как на скриншотах сверху».
  // Heuristic: continent.id starts with 'greenwald' = tidal continent,
  // 'sharpcrest' = anchorless. Fallback to root.id.
  const continentArt = continent.id === 'greenwald' || continent.rootZoneId === 'tidal-spawn'
    ? `${import.meta.env.BASE_URL}maps/continent-tidal.png`
    : `${import.meta.env.BASE_URL}maps/continent-anchorless.png`;

  return (
    <svg
      width={VIEW_W}
      height={VIEW_H}
      viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
      style={{ display: 'block' }}
    >
      <defs>
        <pattern id="wm-continent-bg" patternUnits="userSpaceOnUse" width="220" height="220">
          <rect width="220" height="220" fill="#1a1208" />
        </pattern>
      </defs>
      {/* Тёмный задник под изображение (виден по краям если карта не квадрат). */}
      <rect x={0} y={0} width={VIEW_W} height={VIEW_H} fill="url(#wm-continent-bg)" />
      {/* Hand-painted parchment continent map (Nano Banana Pro). */}
      <image
        href={continentArt}
        x={0}
        y={0}
        width={VIEW_W}
        height={VIEW_H}
        preserveAspectRatio="xMidYMid meet"
      />

      {/* Подпись континента в углу */}
      <text
        x={20}
        y={36}
        fill="rgba(255,228,140,0.85)"
        fontFamily="var(--aiwari-font-display)"
        fontSize={22}
        letterSpacing="0.08em"
        style={{ filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.7))' }}
      >
        {continent.name.ru}
      </text>

      {/* Organic polygon hit-areas + glow border on hover. */}
      {childZones.map((z) => {
        const tl = worldToView(z.bounds.minX, z.bounds.minZ, root.bounds, VIEW_W, VIEW_H);
        const br = worldToView(z.bounds.maxX, z.bounds.maxZ, root.bounds, VIEW_W, VIEW_H);
        const w = Math.max(8, br.x - tl.x);
        const h = Math.max(8, br.y - tl.y);
        const isHover = hoverZoneId === z.id;
        const isPlayerZone = playerZoneId === z.id;
        const points = organicPolygonPoints(z.id, tl.x, tl.y, w, h, 16);
        const cx = tl.x + w / 2;
        const cy = tl.y + h / 2;
        return (
          <g
            key={z.id}
            onMouseEnter={() => setHoverZoneId(z.id)}
            onMouseLeave={() => setHoverZoneId(null)}
            onClick={() => onZoneClick(z.id)}
            style={{ cursor: 'pointer' }}
          >
            {/* Invisible hit-area полигон. На hover — золотой контур по
             *  границам зоны (НЕ квадрат, organic outline). */}
            <polygon
              points={points}
              fill={isHover ? 'rgba(250,179,0,0.10)' : 'transparent'}
              stroke={isHover ? '#FAB300' : (isPlayerZone ? 'rgba(250,179,0,0.55)' : 'transparent')}
              strokeWidth={isHover ? 2.5 : (isPlayerZone ? 1.5 : 0)}
              style={isHover ? { filter: 'drop-shadow(0 0 8px rgba(250,179,0,0.9))' } : undefined}
            />
            {/* Имя зоны под курсором появляется только при hover —
             *  чтобы не загромождать handpainted карту лейблами. */}
            {isHover && (
              <text
                x={cx}
                y={cy + 5}
                textAnchor="middle"
                fontFamily="var(--aiwari-font-display)"
                fontSize={Math.max(11, Math.min(15, w / 8))}
                fill="#FFEB9C"
                style={{
                  pointerEvents: 'none',
                  filter: 'drop-shadow(0 1px 3px rgba(0,0,0,0.95)) drop-shadow(0 0 6px rgba(0,0,0,0.8))',
                }}
              >
                {z.name.ru}
              </text>
            )}
            {/* Маркер игрока — компактный золотой шеврон по центру зоны. */}
            {isPlayerZone && (
              <g transform={`translate(${cx}, ${cy})`} style={{ pointerEvents: 'none' }}>
                <circle r={9} fill="rgba(250,179,0,0.25)" stroke="#FAB300" strokeWidth={1.5} />
                <polygon points="0,-5 4,4 0,2 -4,4" fill="#0F0900" />
              </g>
            )}
          </g>
        );
      })}

      {/* Floating tooltip with full zone info — bottom-right, на hover. */}
      {tooltip && (
        <g style={{ pointerEvents: 'none' }}>
          <rect
            x={VIEW_W - 280}
            y={VIEW_H - 60}
            width={260}
            height={48}
            rx={4}
            fill="rgba(15,9,0,0.85)"
            stroke="rgba(250,179,0,0.55)"
            strokeWidth={1}
          />
          <text
            x={VIEW_W - 20}
            y={VIEW_H - 36}
            textAnchor="end"
            fontFamily="var(--aiwari-font-display)"
            fontSize={16}
            fill="#FAB300"
          >
            {tooltip.name.ru}
          </text>
          <text
            x={VIEW_W - 20}
            y={VIEW_H - 18}
            textAnchor="end"
            fontFamily="var(--aiwari-font-body)"
            fontSize={11}
            fill="rgba(255,228,140,0.85)"
          >
            Уровни {tooltip.recommendedLevel.min}–{tooltip.recommendedLevel.max} · {pvpLabel(tooltip.pvp)}
          </text>
        </g>
      )}
    </svg>
  );
}

// ─── Level 3: World view ─────────────────────────────────────────────────
interface WorldViewProps {
  continents: Continent[];
  hoverContinentId: string | null;
  setHoverContinentId: (id: string | null) => void;
  playerContinentId: string | null;
  onContinentClick: (continentId: string) => void;
}

function WorldView({
  continents, hoverContinentId, setHoverContinentId, playerContinentId, onContinentClick,
}: WorldViewProps) {
  // Левый и правый континент с морем посередине. Просто два «ушка».
  const padX = 60;
  const padY = 60;
  const seaW = 80;
  const contW = (VIEW_W - padX * 2 - seaW) / 2;
  const contH = VIEW_H - padY * 2;

  const slots: Array<{ continent: Continent; x: number; y: number; w: number; h: number }> = [];
  if (continents[0]) slots.push({ continent: continents[0], x: padX, y: padY, w: contW, h: contH });
  if (continents[1]) slots.push({ continent: continents[1], x: padX + contW + seaW, y: padY, w: contW, h: contH });

  return (
    <svg
      width={VIEW_W}
      height={VIEW_H}
      viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
      style={{ display: 'block' }}
    >
      <defs>
        <linearGradient id="wm-sea" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#0a1a2a" />
          <stop offset="100%" stopColor="#040a14" />
        </linearGradient>
        <pattern id="wm-world-bg" patternUnits="userSpaceOnUse" width="240" height="240">
          <rect width="240" height="240" fill="#0c0a08" />
          <circle cx="80" cy="100" r="60" fill="rgba(60,40,20,0.18)" />
          <circle cx="200" cy="180" r="80" fill="rgba(60,40,20,0.12)" />
        </pattern>
      </defs>

      {/* Море/океан */}
      <rect x={0} y={0} width={VIEW_W} height={VIEW_H} fill="url(#wm-sea)" />
      <rect x={0} y={0} width={VIEW_W} height={VIEW_H} fill="url(#wm-world-bg)" opacity={0.5} />

      {/* Заголовок */}
      <text
        x={VIEW_W / 2}
        y={36}
        textAnchor="middle"
        fill="rgba(232,199,123,0.6)"
        fontFamily="var(--aiwari-font-display)"
        fontSize={24}
        letterSpacing="0.12em"
      >
        Известный мир
      </text>

      {/* R232q86zbp: единая painted world-map (Nano Banana Pro) с двумя
       *  континентами и проливом посередине. Клик-зоны — левая и
       *  правая половины. Hover — золотой outline вокруг половины. */}
      <image
        href={`${import.meta.env.BASE_URL}maps/world-known.png`}
        x={20}
        y={48}
        width={VIEW_W - 40}
        height={VIEW_H - 96}
        preserveAspectRatio="xMidYMid meet"
      />
      {slots.map(({ continent, x, y, w, h }, idx) => {
        const isHover = hoverContinentId === continent.id;
        const isPlayer = playerContinentId === continent.id;
        // Половинки world-image кликабельны: левая — tidal, правая — anchorless.
        // Прямоугольник полупрозрачный — image сквозь него виден.
        return (
          <g
            key={continent.id}
            onMouseEnter={() => setHoverContinentId(continent.id)}
            onMouseLeave={() => setHoverContinentId(null)}
            onClick={() => onContinentClick(continent.id)}
            style={{ cursor: 'pointer' }}
          >
            <rect
              x={x}
              y={y}
              width={w}
              height={h}
              fill={isHover ? 'rgba(250,179,0,0.10)' : 'transparent'}
              stroke={isHover ? '#FAB300' : 'transparent'}
              strokeWidth={isHover ? 2.5 : 0}
              style={isHover ? { filter: 'drop-shadow(0 0 12px rgba(250,179,0,0.7))' } : undefined}
            />
            <text
              x={x + w / 2}
              y={VIEW_H - 24}
              textAnchor="middle"
              fontFamily="var(--aiwari-font-display)"
              fontSize={18}
              fill="#FFEB9C"
              letterSpacing="0.08em"
              style={{
                pointerEvents: 'none',
                filter: 'drop-shadow(0 1px 3px rgba(0,0,0,0.95)) drop-shadow(0 0 6px rgba(0,0,0,0.8))',
              }}
            >
              {continent.name.ru}
            </text>
            {isPlayer && (
              <g transform={`translate(${x + w / 2}, ${y + 22})`} style={{ pointerEvents: 'none' }}>
                <circle r={9} fill="rgba(250,179,0,0.25)" stroke="#FAB300" strokeWidth={1.5} />
                <polygon points="0,-5 4,4 0,2 -4,4" fill="#0F0900" />
              </g>
            )}
            {void idx /* silence unused */ }
          </g>
        );
      })}
    </svg>
  );
}

// ─── Helpers ─────────────────────────────────────────────────────────────
function pvpLabel(p: 'safe' | 'contested' | 'warzone'): string {
  if (p === 'safe') return 'Мирная';
  if (p === 'contested') return 'Спорная';
  return 'Боевая';
}

// ─── POI inline glyphs ───────────────────────────────────────────────────
function PoiGlyph({ kind }: { kind: ZonePoiKind }) {
  switch (kind) {
    case 'questgiver':
      return (
        <g>
          <circle r={9} fill="rgba(0,0,0,0.65)" stroke="#FAB300" strokeWidth={1.4} />
          <text textAnchor="middle" y={4} fontFamily="var(--aiwari-font-display)" fontSize={12} fontWeight={700} fill="#FAB300">!</text>
        </g>
      );
    case 'turnin':
      return (
        <g>
          <circle r={9} fill="rgba(0,0,0,0.65)" stroke="#FAB300" strokeWidth={1.4} />
          <text textAnchor="middle" y={4} fontFamily="var(--aiwari-font-display)" fontSize={12} fontWeight={700} fill="#FAB300">?</text>
        </g>
      );
    case 'city':
      return (
        <g>
          <polygon
            points="0,-10 2.5,-3 10,-3 4,2 6.5,9 0,5 -6.5,9 -4,2 -10,-3 -2.5,-3"
            fill="#FAB300"
            stroke="#0F0900"
            strokeWidth={1.1}
          />
        </g>
      );
    case 'flight':
      return (
        <g>
          <circle r={5.5} fill="#FAB300" stroke="#0F0900" strokeWidth={1.1} />
          <circle r={2} fill="#0F0900" />
        </g>
      );
    case 'dungeon':
      return (
        <g>
          <path
            d="M -8 6 L -8 -2 Q -8 -8 0 -8 Q 8 -8 8 -2 L 8 6 Z"
            fill="#7f1d1d"
            stroke="#FAB300"
            strokeWidth={1.2}
          />
          <line x1={0} y1={-8} x2={0} y2={6} stroke="#0F0900" strokeWidth={1.1} />
        </g>
      );
    case 'mail':
      return (
        <g>
          <rect x={-7} y={-5} width={14} height={10} fill="#1a1410" stroke="#FAB300" strokeWidth={1.2} />
          <polyline points="-7,-5 0,2 7,-5" fill="none" stroke="#FAB300" strokeWidth={1.2} />
        </g>
      );
    case 'trainer':
      return (
        <g>
          <rect x={-7} y={-6} width={14} height={11} fill="#2a1a0c" stroke="#FAB300" strokeWidth={1.2} />
          <line x1={0} y1={-6} x2={0} y2={5} stroke="#FAB300" strokeWidth={1.1} />
        </g>
      );
    default:
      return null;
  }
}

/**
 * R232q22 Phase 3: глиф для динамических Quest POI. Похож на статичные
 * `PoiGlyph(questgiver|turnin)`, но крупнее (14 px) и с мягким золотым/
 * серым свечением для скан-а на полноэкранной карте.
 */
function DynamicQuestGlyph({ kind }: { kind: 'available' | 'turnin' | 'in-progress' }) {
  if (kind === 'available') {
    return (
      <g>
        <circle r={11} fill="rgba(0,0,0,0.65)" stroke="#FAB300" strokeWidth={1.6} />
        <text
          textAnchor="middle"
          y={5}
          fontFamily="var(--aiwari-font-display)"
          fontSize={14}
          fontWeight={800}
          fill="#FAB300"
          style={{ filter: 'drop-shadow(0 0 4px rgba(250,179,0,0.55))' }}
        >!</text>
      </g>
    );
  }
  if (kind === 'turnin') {
    return (
      <g>
        <circle r={11} fill="rgba(0,0,0,0.65)" stroke="#FAB300" strokeWidth={1.6} />
        <text
          textAnchor="middle"
          y={5}
          fontFamily="var(--aiwari-font-display)"
          fontSize={14}
          fontWeight={800}
          fill="#FAB300"
          style={{ filter: 'drop-shadow(0 0 4px rgba(250,179,0,0.55))' }}
        >?</text>
      </g>
    );
  }
  // in-progress — серый «?»
  return (
    <g>
      <circle r={10} fill="rgba(0,0,0,0.55)" stroke="#9CA3AF" strokeWidth={1.4} />
      <text
        textAnchor="middle"
        y={5}
        fontFamily="var(--aiwari-font-display)"
        fontSize={13}
        fontWeight={700}
        fill="#C8C8C8"
      >?</text>
    </g>
  );
}

function LegendDot({ kind }: { kind: ZonePoiKind }) {
  return (
    <span className="wm-legend-dot">
      <svg width="14" height="14" viewBox="-10 -10 20 20" aria-hidden style={{ verticalAlign: 'middle' }}>
        <PoiGlyph kind={kind} />
      </svg>
      <span>{POI_LABELS[kind]}</span>
    </span>
  );
}
